// src/components/Dashboard/MainComparison.jsx

import React from "react";
import { Box, Typography, Badge, styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import { keyframes, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const keySlideRight = keyframes`
0% {
    -webkit-transform: rotateY(-70deg);
            transform: rotateY(-70deg);
    -webkit-transform-origin: left;
            transform-origin: left;
    opacity: 0;
  }
  100% {
    -webkit-transform: rotateY(0);
            transform: rotateY(0);
    -webkit-transform-origin: left;
            transform-origin: left;
    opacity: 1;
  }
`;

const MainComparison = ({ title, result, Icon, isBold, secs, trigger }) => {
  const theme = useTheme();

  return (
    <StyledBox
      container
      spacing={2}
      alignItems="center"
      secs={secs}
      trigger={trigger}
    >
      {/* Icon Section */}
      <Box
        xs={12}
        sm={3}
        sx={{
          display: "flex",
          flexGrow: 1,
          alignItems: {
            xs: "center",
            sm: "flex-start",
            md: "flex-start",
          },
          justifyContent: "center",
        }}
      >
        {isBold ? (
          <Badge
            overlap="circular"
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            badgeContent={
              <CheckCircleIcon
                sx={{
                  color: "green",
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  border: "2px solid white",
                  boxShadow: "0 0 2px rgba(0,0,0,0.5)",
                  zIndex: 10,
                }}
              />
            }
          >
            <Icon
              aria-label={`${title} Icon`}
              role="img"
              style={{
                width: 60,
                height: 60,
                fill: theme.palette.primary.main,
              }}
            />
          </Badge>
        ) : (
          <Icon
            aria-label={`${title} Icon`}
            role="img"
            style={{
              width: 60,
              height: 60,
              fill: theme.palette.primary.main,
            }}
          />
        )}
      </Box>

      {/* Text Section */}
      <Grid
        item
        xs={12}
        sm={9}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: {
            xs: "center",
            sm: "flex-start",
            md: "flex-start",
          },
          justifyContent: "center",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              fontSize: "clamp(0.8rem, 2vw + 0.5rem, 1.2rem)",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              color: "grey.700",
              fontWeight: isBold ? "bold" : "normal", // Conditional bolding
              textAlign: {
                xs: "center",
                sm: "left",
                md: "left",
              },
            }}
          >
            {result}
          </Typography>
        </Box>
      </Grid>
    </StyledBox>
  );
};

const StyledBox = styled(Box)(({ secs, trigger }) => ({
  display: "flex",
  flexDirection: "row",
  flexGrow: 1,
  alignItems: "center",
  backgroundColor: "",
  animation: trigger
    ? `${keySlideRight} 1s cubic-bezier(0.175, 0.885, 0.320, 1.275) both`
    : "none",
  animationDelay: `${secs}s`,
}));

MainComparison.propTypes = {
  title: PropTypes.string.isRequired,
  result: PropTypes.node.isRequired,
  Icon: PropTypes.elementType.isRequired,
  isBold: PropTypes.bool, // New prop for bolding
};

MainComparison.defaultProps = {
  isBold: false, // Default to not bold
};

export default MainComparison;
